import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    OnDestroy,
    inject,
    ModelSignal,
    model,
    ViewEncapsulation,
    Signal,
    computed,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";
import {
    PricingPackage,
    PricingPackagesService,
    PricingPackageType,
    DEFAULT_PACKAGE,
} from "../../_services/pricing-packages.service";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-pricing-package-edit-dialog",
    templateUrl: "./pricing-package-edit-dialog.component.html",
    styleUrls: ["./pricing-package-edit-dialog.component.scss"],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class PricingPackageEditDialogComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();

    readonly data = inject<any>(MAT_DIALOG_DATA);
    readonly packageId: ModelSignal<string> = model<string>(this.data.packageId);
    readonly isNew: Signal<boolean> = computed(() => {
        const id = this.packageId();
        return id === undefined || id === null || id.length === 0 || id === "new";
    });
    readonly pricingPackage: ModelSignal<PricingPackage> = model<PricingPackage>(DEFAULT_PACKAGE);

    readonly isComplete: Signal<boolean> = computed(() => {
        const pricingPackage = this.pricingPackage();
        return pricingPackage !== null && pricingPackage.name.length > 0;
    });

    get pricingPackageTypes(): number[] {
        return Object.values(PricingPackageType).filter((value) => typeof value === "number") as number[];
    }

    constructor(
        private notificationService: NotificationsService,
        private pricingPackagesService: PricingPackagesService,
        public dialogRef: MatDialogRef<PricingPackageEditDialogComponent>,
    ) {}

    ngOnInit(): void {
        if (this.isNew()) {
            return;
        }

        this.pricingPackagesService
            .get(this.packageId()!)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((pricingPackage) => {
                this.pricingPackage.set(pricingPackage);
            });
    }

    create(): void {
        this.pricingPackagesService
            .create(this.pricingPackage())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res) => {
                this.notificationService.showSuccess("Success", "Pricing package created successfully");
                this.dialogRef.close(res);
            });
    }

    update(): void {
        this.pricingPackagesService
            .update(this.packageId()!, this.pricingPackage())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res) => {
                this.notificationService.showSuccess("Success", "Pricing package updated successfully");
                this.dialogRef.close(res);
            });
    }

    onPackageUpdated(): void {
        this.pricingPackage.set({ ...this.pricingPackage() });
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
